import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { passwordResetAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, Calendar, FileText, FolderLock, Sparkles, ArrowLeft, Mail, KeyRound, Check, Globe2 } from 'lucide-react';
import { toast } from 'sonner';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_99de4320-286b-45dc-9267-85b0447b3219/artifacts/ijyqyuec_LexAgenda.png';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_860f89f1-5bcd-47ca-9a42-2845ecac666a/artifacts/4om46ky3_LexAgenda-final_logo.png';

const FEATURES = [
  { icon: Calendar, title: 'Meetings & Governance', desc: 'Plan Board and Committee meetings, ASM, and SSM. Use the Governance Hub to share documents for review, conduct polls and voting, and manage collaborative approvals across all stakeholders' },
  { icon: FileText, title: 'Document Generation', desc: 'Generate notices, agendas, minutes, resolutions, and Secretary\u2019s Certificates from reusable templates' },
  { icon: FolderLock, title: 'Library & Records', desc: 'Manage a centralized document repository with Corporate Books, Meeting Docs, Governance files, Regulatory Docs, and Communications with classification levels' },
  { icon: Sparkles, title: 'AI-Assisted Drafting', desc: 'Use AI to help draft minutes, resolutions, and certificates under your control' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(null); // null, 'email', 'code', 'newpass', 'done'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [devCode, setDevCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    setResetError('');
    setResetLoading(true);
    try {
      const res = await passwordResetAPI.forgotPassword(resetEmail);
      if (res.data.code_for_dev) {
        setDevCode(res.data.code_for_dev);
      }
      setForgotMode('code');
      toast.success('Reset code sent! Check your email.');
    } catch (err) {
      setResetError('Failed to send reset code');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setResetError('');
    setResetLoading(true);
    try {
      await passwordResetAPI.verifyCode(resetEmail, resetCode);
      setForgotMode('newpass');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      await passwordResetAPI.resetPassword(resetEmail, resetCode, newPassword);
      setForgotMode('done');
      toast.success('Password reset successfully!');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const renderForgotPassword = () => {
    if (forgotMode === 'done') {
      return (
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-[#1A1D21] mb-2">Password Reset!</h2>
          <p className="text-sm text-[#5A6570] mb-6">Your password has been reset successfully. You can now sign in.</p>
          <Button onClick={() => { setForgotMode(null); setResetEmail(''); setResetCode(''); setNewPassword(''); setConfirmPassword(''); }}
            className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10" data-testid="back-to-login-btn">
            Back to Login
          </Button>
        </div>
      );
    }

    return (
      <div>
        <button onClick={() => setForgotMode(null)} className="flex items-center gap-1 text-sm text-[#5A6570] hover:text-[#1A1D21] mb-4" data-testid="back-to-login-link">
          <ArrowLeft className="h-4 w-4" /> Back to Login
        </button>

        {forgotMode === 'email' && (
          <>
            <div className="w-12 h-12 rounded-full bg-[#00A3A3]/10 flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-[#00A3A3]" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1D21]" data-testid="forgot-title">Forgot Password?</h2>
            <p className="text-sm text-[#5A6570] mb-5">Enter your email address and we'll send you a verification code from <strong>support@lexdoright.com</strong></p>
            {resetError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                <AlertCircle className="h-4 w-4 shrink-0" />{resetError}
              </div>
            )}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email">Email Address</Label>
                <Input id="reset-email" type="email" placeholder="your@email.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                  className="border-[#E2E6EA] focus:border-[#00A3A3] h-10" data-testid="forgot-email-input" />
              </div>
              <Button onClick={handleForgotSubmit} disabled={resetLoading || !resetEmail} className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10" data-testid="send-code-btn">
                {resetLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send Reset Code'}
              </Button>
            </div>
          </>
        )}

        {forgotMode === 'code' && (
          <>
            <div className="w-12 h-12 rounded-full bg-[#00A3A3]/10 flex items-center justify-center mb-4">
              <KeyRound className="h-6 w-6 text-[#00A3A3]" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1D21]">Enter Verification Code</h2>
            <p className="text-sm text-[#5A6570] mb-1">We sent a 6-digit code to <strong>{resetEmail}</strong></p>
            <p className="text-xs text-[#A8B1B9] mb-5">Code expires in 15 minutes</p>
            {devCode && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm mb-4">
                <strong>Dev mode:</strong> SMTP not configured. Your code is: <span className="font-mono font-bold text-lg">{devCode}</span>
              </div>
            )}
            {resetError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                <AlertCircle className="h-4 w-4 shrink-0" />{resetError}
              </div>
            )}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reset-code">Verification Code</Label>
                <Input id="reset-code" type="text" placeholder="000000" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="border-[#E2E6EA] focus:border-[#00A3A3] h-10 text-center text-2xl font-mono tracking-[0.5em]" maxLength={6} data-testid="verify-code-input" />
              </div>
              <Button onClick={handleVerifyCode} disabled={resetLoading || resetCode.length !== 6} className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10" data-testid="verify-code-btn">
                {resetLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : 'Verify Code'}
              </Button>
            </div>
          </>
        )}

        {forgotMode === 'newpass' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <KeyRound className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1D21]">Set New Password</h2>
            <p className="text-sm text-[#5A6570] mb-5">Enter your new password below</p>
            {resetError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                <AlertCircle className="h-4 w-4 shrink-0" />{resetError}
              </div>
            )}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-pass">New Password</Label>
                <Input id="new-pass" type="password" placeholder="Min 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="border-[#E2E6EA] focus:border-[#00A3A3] h-10" data-testid="new-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pass">Confirm Password</Label>
                <Input id="confirm-pass" type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="border-[#E2E6EA] focus:border-[#00A3A3] h-10" data-testid="confirm-password-input" />
              </div>
              <Button onClick={handleResetPassword} disabled={resetLoading || !newPassword || !confirmPassword} className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10" data-testid="reset-password-btn">
                {resetLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</> : 'Reset Password'}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="auth-page">
      {/* Features Panel — visible on all screens */}
      <div className="relative overflow-hidden lg:w-1/2 lg:flex">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.70]" />
        <div className="absolute inset-0" style={{ background: 'rgba(0,140,160,0.25)', mixBlendMode: 'overlay' }} />
        <div className="absolute inset-0" style={{ background: 'rgba(80,100,110,0.30)', mixBlendMode: 'multiply' }} />

        <div className="relative z-10 flex flex-col w-full px-6 py-8 lg:justify-end lg:p-10 xl:p-14">
          {/* Mobile: logo + branding */}
          <div className="flex flex-col items-center mb-5 lg:hidden">
            <img src={LOGO_URL} alt="LexAgenda" className="h-28 object-contain mb-3" />
            <h2 className="text-white text-lg font-bold text-center" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
              Corporate Governance Made Simple
            </h2>
          </div>

          <div className="space-y-2.5 lg:space-y-3">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="flex flex-col rounded-2xl px-4 py-3 lg:px-5 lg:py-4"
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(18px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(18px) saturate(180%)',
                  border: '1px solid rgba(255,255,255,0.30)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.35)',
                }}
              >
                <div className="flex items-center gap-2 rounded-xl px-2.5 py-1 lg:px-3 lg:py-1.5 mb-1" style={{ background: 'rgba(245,138,38,0.22)' }}>
                  <f.icon className="h-4 w-4 text-[#F58A26] shrink-0" />
                  <h3 className="font-bold text-base lg:text-xl text-[#F58A26]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.3)' }}>{f.title}</h3>
                </div>
                <div className="pl-1">
                  <p className="text-white text-sm lg:text-base leading-relaxed font-bold" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-white/60 font-bold text-center lg:text-left" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            Aligned with the Revised Corporation Code (RA 11232) and SEC regulations
          </p>
        </div>
      </div>

      {/* Login Panel */}
      <div className="flex-1 flex flex-col items-center justify-center bg-[#FAFBFC] px-6 py-10">
        {/* Logo — only visible on desktop */}
        <img src={LOGO_URL} alt="LexAgenda" className="hidden lg:block h-72 xl:h-80 mb-8 object-contain" data-testid="auth-logo" />

        <div className="w-full max-w-sm">
          {forgotMode ? (
            <div className="bg-white rounded-xl border border-[#E2E6EA] shadow-sm p-6">
              {renderForgotPassword()}
            </div>
          ) : (
            <>
              <div className="flex rounded-lg border border-[#E2E6EA] overflow-hidden mb-8" data-testid="auth-tabs">
                <div className="flex-1 py-2.5 text-center text-sm font-medium bg-white text-[#1A1D21] border-b-2 border-[#00A3A3]" data-testid="tab-login">
                  Login
                </div>
                <Link to="/register" className="flex-1 py-2.5 text-center text-sm font-medium bg-[#F8F9FA] text-[#5A6570] hover:text-[#1A1D21] transition-colors" data-testid="tab-register">
                  Register
                </Link>
              </div>

              <div className="bg-white rounded-xl border border-[#E2E6EA] shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#1A1D21]" data-testid="form-title">Welcome Back</h2>
                <p className="text-sm text-[#5A6570] mb-6">Enter your credentials to access your account</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-error">
                      <AlertCircle className="h-4 w-4 shrink-0" />{error}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium text-[#1A1D21]">Email</Label>
                    <Input id="email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required
                      data-testid="login-email-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] h-10" />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-medium text-[#1A1D21]">Password</Label>
                      <button type="button" onClick={() => { setForgotMode('email'); setResetEmail(email); }}
                        className="text-xs text-[#00A3A3] hover:text-[#008585] font-medium" data-testid="forgot-password-link">
                        Forgot Password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required
                        data-testid="login-password-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] pr-10 h-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6570] hover:text-[#1A1D21] transition-colors"
                        data-testid="login-password-toggle" tabIndex={-1}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10" disabled={loading} data-testid="login-submit-btn">
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Login'}
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
